import { Typography, makeStyles } from '@material-ui/core'
import React, { useState } from 'react'
import { useSelector } from 'react-redux'
import { Highlighted } from '../../root/components/common/highlighted'
import CustomButton from '../../root/components/common/button'
import { TableVirtualized } from '../../root/components/common/table-virtualized/table'
import { UserPermissions } from 'web/src/services/permissions'
import { SIM_CARD_STATUS, STAND_BY_FUSION_VALUE } from '../../root/constants'
import { NavLink } from 'react-router-dom'
import SimCardStatus from './sim-card-status'
import { AddSimCardModal } from './add-sim-card-modal'
import { validateIccid } from 'web/src/utils'

export default function SimCardsTable({
    simCards,
    search,
    sorting,
    onSort,
    isLoading,
    onLoaderMore,
    updateSimCard,
    createSimCard,
    isCreatingSimCard,
    isSimCardCreationSuccessful,
    isSimCardCreationError
}) {
    const classes = useStyles()
    const { locale } = useSelector(state => state.main)

    const [simCardSerialNumber, setSimCardSerialNumber] = useState('')
    const [simCardError, setSimCardError] = useState('')

    const handleSimCardActiveClick = simSerialNumber => {
        updateSimCard({ simSerialNumber, status: SIM_CARD_STATUS.active })
    }

    const handleSimCardDeactivateClick = simSerialNumber => {
        updateSimCard({ simSerialNumber, status: SIM_CARD_STATUS.deactive })
    }

    const handleSaveSimCard = networkDeviceId => {
        if (!simCardSerialNumber) {
            setSimCardError(locale.simCards.simCardEmptyError)
            return
        }

        if (!validateIccid(simCardSerialNumber)) {
            setSimCardError(locale.simCards.simCardNotValidIccidError)
            return
        }

        setSimCardError('')
        createSimCard({
            simSerialNumber: simCardSerialNumber,
            device_id: networkDeviceId
        })
    }

    const handleCancelSimCard = () => {
        setSimCardSerialNumber('')
        setSimCardError('')
    }

    const columns = [
        {
            key: 'network_device_serial_number',
            label: locale.locations.networkDevice,
            sortable: true,
            dataType: 'string',
            width: 1.5,
            minWidth: 140,
            render: ({ rowData }) => {
                return (
                    <Typography
                        className={classes.cellDiv}
                        variant="inherit"
                        noWrap>
                        <NavLink
                            className={classes.link}
                            to={`/network-devices/${rowData.network_device_id}`}>
                            <Highlighted
                                text={rowData.networkDeviceSerialNumber}
                                highlight={search}
                            />
                        </NavLink>
                    </Typography>
                )
            }
        },
        {
            key: 'location_name',
            label: locale.devices.location,
            sortable: true,
            dataType: 'string',
            width: 1.5,
            minWidth: 200,
            render: ({ rowData, cellData }) => (
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    <Typography
                        className={classes.cellDiv}
                        variant="inherit"
                        noWrap>
                        <NavLink
                            className={classes.link}
                            to={`/locations/${rowData.location_id}/overview`}>
                            <Highlighted
                                text={rowData.location_name}
                                highlight={search}
                            />
                        </NavLink>
                    </Typography>
                </div>
            )
        },
        {
            key: 'sim_serial_number',
            label: locale.simCards.simCardSerialNumber,
            sortable: true,
            dataType: 'string',
            width: 1.5,
            minWidth: 140,
            render: ({ rowData, index }) => {
                return rowData.simSerialNumber ? (
                    <Typography
                        className={classes.cellDiv}
                        variant="inherit"
                        noWrap>
                        <Highlighted
                            text={rowData.simSerialNumber ?? ''}
                            highlight={search}
                        />
                    </Typography>
                ) : (
                    <AddSimCardModal
                        key={index}
                        locale={locale}
                        simCardSerialNumber={simCardSerialNumber}
                        setSimCardSerialNumber={setSimCardSerialNumber}
                        simCardError={simCardError}
                        setSimCardError={setSimCardError}
                        handleSaveSimCard={() =>
                            handleSaveSimCard(rowData.network_device_id)
                        }
                        handleCancelSimCard={handleCancelSimCard}
                        isCreatingSimCard={isCreatingSimCard}
                        isSimCardCreationSuccessful={
                            isSimCardCreationSuccessful
                        }
                        isSimCardCreationError={isSimCardCreationError}
                    />
                )
            }
        },
        {
            key: 'status',
            label: locale.locations.status,
            sortable: false,
            dataType: 'string',
            width: 1.5,
            minWidth: 140,
            render: ({ rowData }) => {
                return <SimCardStatus status={rowData.status} locale={locale} />
            }
        },
        {
            key: 'action',
            label: locale.devices.actions,
            sortable: false,
            width: 1.5,
            minWidth: 140,
            alignCenter: true,
            render: ({ rowData }) => {
                const { status } = rowData
                if (
                    !status ||
                    status === SIM_CARD_STATUS.pending ||
                    status === STAND_BY_FUSION_VALUE ||
                    !UserPermissions.device(rowData.customerId).update ||
                    !UserPermissions.isAllOriginalGranted()
                )
                    return

                return status === SIM_CARD_STATUS.active ? (
                    <CustomButton
                        reverted
                        onClick={() => {
                            handleSimCardDeactivateClick(
                                rowData.simSerialNumber
                            )
                        }}>
                        {locale.simCards.deactivate}
                    </CustomButton>
                ) : (
                    <CustomButton
                        onClick={() => {
                            handleSimCardActiveClick(rowData.simSerialNumber)
                        }}>
                        {locale.simCards.activate}
                    </CustomButton>
                )
            }
        }
    ]

    return (
        <TableVirtualized
            data={simCards}
            columns={columns}
            rowHeight={70}
            sorting={sorting}
            onSort={onSort}
            isLoading={isLoading}
            isLoadingMore={isLoading}
            onLoadMore={onLoaderMore}
        />
    )
}

const useStyles = makeStyles(theme => ({
    highlightItemBold: {
        color: theme.textPrimary,
        fontWeight: 700
    },
    cbHotSwap: {
        display: 'flex',
        justifyContent: 'center'
    },
    cbHotSwapChecker: {
        margin: '0'
    },
    cellDiv: {
        display: 'flex',
        flexDirection: 'column'
    },
    labelWrapper: {
        borderRadius: theme.borderRadius,
        fontWeight: 500,
        fontSize: '12px',
        padding: '3px 6px',
        backgroundColor: theme.grayLight
    },
    statusDeactive: {
        color: theme.gray,
        backgroundColor: theme.grayLight
    },
    statusActive: {
        color: theme.green,
        backgroundColor: theme.greenLight
    },
    link: {
        cursor: 'pointer',
        textDecoration: 'none',
        color: theme.primary,
        fontWeight: 'bold'
    },
    tooltipWrapper: {
        cursor: 'pointer'
    }
}))
